'use client';
import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import Image from 'next/image';
import { IMAGE_SIZES, ResponsiveImage } from '@web/molecules';
import { useReporter } from '../../reporter';
import { Modal } from '../Modal';
import { StyledMuxPlayer } from '../MuxVideo/styles.js';
import { getPlainText } from '../../cms/RichText/getPlainText';
import { getVideoPosterSrc } from './getVideoPosterSrc';
import { ImageModalContent, VideoModalContent } from './styles.js';
import { videoPlaying } from './events';

export const StoryCardModal = ({
  activeCard,
  shown,
  onClose,
  onAfterClose,
  parentHeadline = '',
}) => {
  const reporter = useReporter();
  const viewVideoReported = useRef(false);

  if (!activeCard) {
    return null;
  }

  const isVideo = activeCard.mediaType === 'video' && activeCard.video;
  const isImage = activeCard.mediaType === 'image' && activeCard.image;
  const videoPosterSrc = isVideo ? getVideoPosterSrc(activeCard.video) : null;

  if (!isVideo && !isImage) {
    return null;
  }

  const reportVideoViewed = () => {
    if (!viewVideoReported.current)
      reporter.tag(videoPlaying(getPlainText(parentHeadline)));
    viewVideoReported.current = true;
  };

  const onCloseClick = () => {
    onClose();
    viewVideoReported.current = false;
  };

  return (
    <Modal
      shown={shown}
      onCloseModal={onCloseClick}
      onAfterClose={onAfterClose}
      ariaLabel={
        isVideo ? parentHeadline || 'Story card video' : 'Story card image'
      }
      centerModal
      mobileFullScreen={false}
      transparentBackground
      hideCloseBtnCircle
      closeBtnColor="background.base"
    >
      {isVideo ? (
        <VideoModalContent>
          <StyledMuxPlayer
            playbackId={activeCard.video.playbackId}
            aspectRatio="9 / 16"
            autoPlay
            loop={false}
            muted={false}
            showScrollControls
            poster=""
            onPlaying={() => reportVideoViewed()}
          >
            {videoPosterSrc ? (
              <Image
                alt={
                  activeCard.video?.thumbnailAlt ||
                  activeCard.image?.alt ||
                  'Video thumbnail'
                }
                slot="poster"
                src={videoPosterSrc}
                width={720}
                height={1280}
                sizes={IMAGE_SIZES.modalNearFull}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : null}
          </StyledMuxPlayer>
        </VideoModalContent>
      ) : (
        <ImageModalContent>
          <ResponsiveImage
            {...activeCard.image}
            sizes={IMAGE_SIZES.modalNearFull}
          />
        </ImageModalContent>
      )}
    </Modal>
  );
};

StoryCardModal.displayName = 'StoryCardModal';

StoryCardModal.propTypes = {
  activeCard: PropTypes.shape({
    mediaType: PropTypes.oneOf(['video', 'image']),
    video: PropTypes.shape({
      playbackId: PropTypes.string,
      thumbnailAlt: PropTypes.string,
    }),
    image: PropTypes.object,
  }),
  shown: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAfterClose: PropTypes.func,
  parentHeadline: PropTypes.string,
};
